import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createElementVNode as _createElementVNode, withCtx as _withCtx, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "gap-5 grid" }
const _hoisted_2 = { class: "pt-8 gap-5 grid" }
const _hoisted_3 = { key: 2 }
const _hoisted_4 = { key: 3 }
const _hoisted_5 = {
  key: 4,
  class: "text-center"
}

import { onMounted, ref } from 'vue';
import { useMsal } from '@/composables/useMsal';
import { Link, RichText } from '@sitecore-jss/sitecore-jss-vue';
import DateOfBirth from '@/components/DateInput/DateOfBirth.vue';
import Divider from '@/components/Divider/Divider.vue';
import InformationBox from '@/components/InformationBox/InformationBox.vue';
import OktaLayout from '@/components/OktaLayout/OktaLayout.vue';
import PanelTitle from '@/components/PanelTitle/PanelTitle.vue';
import RACQFormKit from '@/components/RACQFormKit/RACQFormKit.vue';
import Tag from '@/components/Tag/Tag.vue';
import useFormInputData from '@/composables/useFormInputData';
import { ISO_DATE, STANDARD_DATE_FORMAT } from '@/constants/formats';
import { FormEvents } from '@/enums/form-events.enum';
import { TagSize, TagTypes } from '@/enums/tag-enums';
import { ISitecoreFormGenericInput } from '@/interfaces';
import { InformationBoxTypes } from '@/interfaces/forms/information-box.interface';
import { useAccountStore } from '@/store';
import { useAnalyticsStore } from '@/store';
import { useUsernameRecoveryStore } from '@/store/username-recovery/username-recovery.state';
import { convertArrayToObject, dateStringToDate, dateToDateString } from '@/utils/data-transforms';
import { MatchStatus } from '@/interfaces/self-service/search-identity.interface';

const FORM_NAME = 'Recover login details';

enum PageStates {
  MEMBER_DETAILS = 'memberDetails',
  MEMBERSHIP_CARD_NUMBER = 'membershipCardNumber',
  SUCCESS = 'success',
  FAILED = 'failed',
  LOCKED = 'locked',
}


export default /*@__PURE__*/_defineComponent({
  __name: 'UsernameRecovery',
  props: {
    content: {},
    id: {},
    name: {}
  },
  setup(__props: any) {

const props = __props;
const formInputs = convertArrayToObject(props.content);
const accountStore = useAccountStore();

const currentState = ref<PageStates>(PageStates.MEMBER_DETAILS);
const partyMatchDetails = ref<Record<string, string>>({});
const membershipCardDetails = ref<Record<string, string>>({});
const identityNotFound = ref(false);
const membershipCardMismatch = ref(false);
const isLoading = ref(false);
const isTooltipOpen = ref(false);
const username = ref('');
const { accounts } = useMsal();

const { formInputData } = useFormInputData(formInputs);
const store = useUsernameRecoveryStore();
const analyticsStore = useAnalyticsStore();

onMounted(() => {
  analyticsStore.pushAnalytics({
    event: FormEvents.START,
    form: {
      authStatus: accounts.value.length === 0 ? 'Not logged in' : 'Logged in',
      name: FORM_NAME,
      stepName: 'FormStarted',
    },
  });
});

const partyMatch = async () => {
  try {
    isLoading.value = true;
    identityNotFound.value = false;
    const dob = dateToDateString(dateStringToDate(partyMatchDetails.value.dob, STANDARD_DATE_FORMAT), ISO_DATE);
    const searchResult = await store.searchIdentities({
      firstName: partyMatchDetails.value.firstName,
      lastName: partyMatchDetails.value.lastName,
      postcode: partyMatchDetails.value.postcode,
      dateOfBirth: dob,
    });

    // Exact match
    if (searchResult.email) {
      completeRecovery(searchResult.email);
    }
    // Other status
    else {
      switch (searchResult.matchStatus) {
        case MatchStatus.NotRegistered:
          currentState.value = PageStates.FAILED;
          break;
        case MatchStatus.NoMatch:
          identityNotFound.value = true;
          break;
        case MatchStatus.Inactive:
          currentState.value = PageStates.LOCKED;
          analyticsStore.pushAnalytics({
            event: FormEvents.JEOPARDY,
            form: {
              authStatus: 'Not logged in',
              name: FORM_NAME,
              formErrorDetail: 'Your account has been locked out due to...',
              formErrorType: 'Locked out',
            },
          });
          break;
        case MatchStatus.MultiMatches:
          currentState.value = PageStates.MEMBERSHIP_CARD_NUMBER;
          analyticsStore.pushAnalytics({
            event: FormEvents.STEP,
            form: {
              authStatus: 'Not logged in',
              name: FORM_NAME,
              stepName: 'Verify with your membership card number',
            },
          });
          break;

        default:
          break;
      }
    }
  } catch {
    identityNotFound.value = true;
  }
  isLoading.value = false;
};

const verifyMembershipCardNumber = async () => {
  isLoading.value = true;
  const dob = dateToDateString(dateStringToDate(partyMatchDetails.value.dob, STANDARD_DATE_FORMAT), ISO_DATE);
  const searchResult = await store.searchIdentities({
    firstName: partyMatchDetails.value.firstName,
    lastName: partyMatchDetails.value.lastName,
    postcode: partyMatchDetails.value.postcode,
    dateOfBirth: dob,
    membershipNumber: membershipCardDetails.value.membershipCardInput?.replaceAll(' ', ''),
  });

  // Membership number mismatch
  if (searchResult.matchStatus == MatchStatus.MemberNumberMismatched) {
    membershipCardMismatch.value = true;
  } else if (searchResult.email) {
    completeRecovery(searchResult.email);
  }
  // Okta account not found
  else {
    currentState.value = PageStates.FAILED;
    analyticsStore.pushAnalytics({
      event: FormEvents.COMPLETE,
      form: {
        authStatus: accounts.value.length === 0 ? 'Not logged in' : 'Logged in',
        name: FORM_NAME,
        field: 'Login details not found',
        stepName: 'FormCompleted',
      },
    });
  }
  isLoading.value = false;
};

const completeRecovery = (email: string) => {
  username.value = email;
  currentState.value = PageStates.SUCCESS;
  analyticsStore.pushAnalytics({
    event: FormEvents.COMPLETE,
    form: {
      authStatus: 'Not logged in',
      name: FORM_NAME,
      field: 'Login details recovered',
      stepName: 'FormCompleted',
    },
  });
};

const goToLoginWithHint = async () => {
  isLoading.value = true;
  const url = await accountStore.getSelfServiceAccountOverviewUrl();
  await accountStore.signInWithHint(url, username.value);
};

const toggleTooltip = () => {
  isTooltipOpen.value = !isTooltipOpen.value;
  if (isTooltipOpen.value) {
    setTimeout(() => {
      document.querySelector('.v-popper__inner')?.addEventListener('click', () => {
        isTooltipOpen.value = false;
      });
    }, 500);
  }
};

return (_ctx: any,_cache: any) => {
  const _component_FormKit = _resolveComponent("FormKit")!
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createBlock(OktaLayout, null, {
    body: _withCtx(() => [
      (currentState.value == PageStates.MEMBER_DETAILS)
        ? (_openBlock(), _createBlock(_component_FormKit, {
            key: 0,
            modelValue: partyMatchDetails.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((partyMatchDetails).value = $event)),
            type: "form",
            onSubmit: partyMatch
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_1, [
                _createVNode(PanelTitle, _normalizeProps(_guardReactiveProps(_unref(formInputs).panelTitle)), null, 16),
                (identityNotFound.value)
                  ? (_openBlock(), _createBlock(InformationBox, {
                      key: 0,
                      type: _unref(InformationBoxTypes).ERROR,
                      description: _unref(formInputs).usernameUnrecoverable?.content?.value
                    }, null, 8, ["type", "description"]))
                  : _createCommentVNode("", true),
                _createVNode(RACQFormKit, {
                  bindings: _unref(formInputData)('firstName')
                }, null, 8, ["bindings"]),
                _createVNode(RACQFormKit, {
                  bindings: _unref(formInputData)('lastName')
                }, null, 8, ["bindings"]),
                _createVNode(DateOfBirth, {
                  bindings: _unref(formInputData)('dob'),
                  invalidDobWarning: _unref(formInputs).dob?.invalidDobWarning?.value,
                  maxDateByDays: _unref(formInputs).dob?.maxDateByDays?.value,
                  minDate: _unref(formInputs).dob?.minDate?.value,
                  dateMustBefore: _unref(formInputs).dob?.dateMustBefore?.value,
                  dateMustAfter: _unref(formInputs).dob?.dateMustAfter?.value
                }, null, 8, ["bindings", "invalidDobWarning", "maxDateByDays", "minDate", "dateMustBefore", "dateMustAfter"]),
                _createVNode(RACQFormKit, {
                  bindings: _unref(formInputData)('postcode')
                }, null, 8, ["bindings"]),
                _createVNode(_component_FormKit, {
                  type: "submit",
                  inputClass: "btn btn-primary w-full",
                  label: _unref(formInputs).nextBtn?.label?.value,
                  loading: isLoading.value,
                  disabled: isLoading.value
                }, null, 8, ["label", "loading", "disabled"])
              ])
            ]),
            _: 1
          }, 8, ["modelValue"]))
        : (currentState.value == PageStates.MEMBERSHIP_CARD_NUMBER)
          ? (_openBlock(), _createBlock(_component_FormKit, {
              key: 1,
              modelValue: membershipCardDetails.value,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((membershipCardDetails).value = $event)),
              type: "form",
              onSubmit: verifyMembershipCardNumber
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_2, [
                  (membershipCardMismatch.value)
                    ? (_openBlock(), _createBlock(InformationBox, {
                        key: 0,
                        type: _unref(InformationBoxTypes).ERROR,
                        description: _unref(formInputs).invalidMembershipNumber?.content?.value
                      }, null, 8, ["type", "description"]))
                    : _createCommentVNode("", true),
                  (membershipCardMismatch.value)
                    ? (_openBlock(), _createBlock(Divider, { key: 1 }))
                    : _createCommentVNode("", true),
                  _createVNode(PanelTitle, _normalizeProps(_guardReactiveProps(_unref(formInputs).verifyMembershipTitle)), null, 16),
                  _createVNode(RACQFormKit, {
                    bindings: _unref(formInputData)('membershipCardInput')
                  }, null, 8, ["bindings"]),
                  _withDirectives(_createVNode(_unref(RichText), {
                    tag: "button",
                    field: _unref(formInputs).whereCanIFIndMyNumber?.label,
                    class: "link w-fit",
                    onClick: toggleTooltip
                  }, null, 8, ["field"]), [
                    [
                      _directive_tooltip,
                      {
              content: _unref(formInputs).whereCanIFIndMyNumber?.helpSm?.value,
              triggers: [],
              shown: isTooltipOpen.value,
              html: true,
            },
                      void 0,
                      { bottom: true }
                    ]
                  ]),
                  _createVNode(_component_FormKit, {
                    type: "submit",
                    inputClass: "btn btn-primary w-full",
                    label: _unref(formInputs).verifyBtn?.label?.value,
                    loading: isLoading.value,
                    disabled: isLoading.value
                  }, null, 8, ["label", "loading", "disabled"])
                ])
              ]),
              _: 1
            }, 8, ["modelValue"]))
          : (currentState.value == PageStates.LOCKED)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _createVNode(InformationBox, {
                  type: _unref(InformationBoxTypes).ERROR,
                  description: _unref(formInputs).accountHasBeenLocked?.content?.value
                }, null, 8, ["type", "description"])
              ]))
            : (currentState.value == PageStates.FAILED)
              ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                  _createVNode(_unref(RichText), {
                    field: _unref(formInputs).accountNotFound?.label
                  }, null, 8, ["field"])
                ]))
              : (currentState.value == PageStates.SUCCESS)
                ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                    _createVNode(_unref(RichText), {
                      field: _unref(formInputs).loginRecovered?.label
                    }, null, 8, ["field"]),
                    _createVNode(Tag, {
                      tagText: username.value,
                      tagType: _unref(TagTypes).person,
                      tagSize: _unref(TagSize).lg,
                      outerClass: "!rounded-full mb-9"
                    }, null, 8, ["tagText", "tagType", "tagSize"]),
                    _createVNode(_component_FormKit, {
                      type: "button",
                      inputClass: "btn btn-primary w-full",
                      label: _unref(formInputs).loginBtn?.label?.value,
                      loading: isLoading.value,
                      disabled: isLoading.value,
                      onClick: goToLoginWithHint
                    }, null, 8, ["label", "loading", "disabled"])
                  ]))
                : _createCommentVNode("", true),
      (currentState.value != PageStates.SUCCESS)
        ? (_openBlock(), _createBlock(_unref(RichText), {
            key: 5,
            field: _unref(formInputs).backToLogin?.label
          }, null, 8, ["field"]))
        : _createCommentVNode("", true)
    ]),
    disclaimer: _withCtx(() => [
      _createVNode(_unref(RichText), {
        field: _unref(formInputs).disclaimer?.label
      }, null, 8, ["field"])
    ]),
    _: 1
  }))
}
}

})